import React, { useCallback, useEffect, useState } from 'react';
import { getDomains, getPageHits, getAdSenseEarnings, getAdImpressions, refreshPageStats } from '../api';
import { refreshPageHits, refreshAdSenseEarnings, refreshAdImpressions } from '../apiAnalytics';
import { useToast } from '../Toast';
import Badge from '../ui/Badge';
import { relativeTime, latestTimestamp } from '../domainMetrics';
import { SOURCES, dataFreshness, asOfLabel } from './doNext';

/* Settings → Data sources: when each cached analytics source was last pulled,
   and a manual refresh for each. The pulls run on a schedule, so these buttons
   are for when a scheduled job missed (the home page's freshness line says so). */

const LOADERS = {
  hits: async () => (await getPageHits()).updated_at || null,
  earnings: async () => (await getAdSenseEarnings()).updated_at || null,
  impressions: async () => (await getAdImpressions()).updated_at || null,
  stats: async () => latestTimestamp(await getDomains(), 'page_stats_updated_at'),
};

const REFRESH = {
  hits: refreshPageHits,
  earnings: refreshAdSenseEarnings,
  impressions: refreshAdImpressions,
  stats: refreshPageStats,
};

const SCHEDULE = {
  hits: 'daily, 3:00 AM Arizona',
  earnings: 'daily, 3:30 AM Arizona',
  impressions: 'daily, 4:00 AM Arizona',
  stats: 'every 6 hours',
};

export default function DataSourcesPanel() {
  const { addToast } = useToast();
  const [updatedAt, setUpdatedAt] = useState({});
  const [failed, setFailed] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});

  const load = useCallback(async (key) => {
    try {
      const value = await LOADERS[key]();
      setUpdatedAt((u) => ({ ...u, [key]: value }));
      setFailed((f) => ({ ...f, [key]: false }));
    } catch (e) {
      setFailed((f) => ({ ...f, [key]: true }));
    }
  }, []);

  useEffect(() => {
    Promise.all(SOURCES.map((s) => load(s.key))).finally(() => setLoading(false));
  }, [load]);

  const refresh = async (source) => {
    setRefreshing((r) => ({ ...r, [source.key]: true }));
    try {
      await REFRESH[source.key]();
      await load(source.key);
      addToast(`${source.label} refreshed`, 'success');
    } catch (err) {
      addToast(`${source.label} refresh failed: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setRefreshing((r) => ({ ...r, [source.key]: false }));
    }
  };

  const now = new Date();
  const { problems } = dataFreshness(updatedAt, failed, now);
  const problemOf = (key) => problems.find((p) => p.key === key);

  return (
    <div>
      <p className="muted small" style={{ marginTop: 0 }}>
        Each source refreshes on its own schedule; refresh one by hand only when it has fallen behind.
      </p>
      {SOURCES.map((s, i) => {
        const problem = !loading && problemOf(s.key);
        return (
          <div
            key={s.key}
            style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
              padding: '8px 0', borderTop: i ? '1px solid var(--color-border)' : 'none',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>
                {s.label}{' '}
                {problem && (
                  <Badge state={problem.state === 'stale' ? 'warn' : 'danger'}>
                    {problem.state === 'stale' ? 'Stale' : problem.state === 'failing' ? 'Failing' : 'Unknown'}
                  </Badge>
                )}
              </div>
              <div className="muted small">
                {loading ? 'Loading…' : updatedAt[s.key]
                  ? `Updated ${asOfLabel(updatedAt[s.key], now)} (${relativeTime(updatedAt[s.key], now)})`
                  : 'No refresh time on record'}
                {' · '}Scheduled {SCHEDULE[s.key]}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={!!refreshing[s.key]}
              onClick={() => refresh(s)}
              title={`Re-pull ${s.label.toLowerCase()} from upstream now`}
            >
              {refreshing[s.key] ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
